import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, ClipboardList, Handshake, Target, TrendingUp,
  Calendar, Users, Sparkles, LogOut, Stethoscope, Upload, Flag, Settings as SettingsIcon,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["founder", "manager", "executive"] },
  { to: "/daily-log", label: "Daily Activity Log", icon: ClipboardList, roles: ["founder", "manager", "executive"] },
  { to: "/tieups", label: "Partner Tie-Ups", icon: Handshake, roles: ["founder", "manager", "executive"] },
  { to: "/events", label: "Events & Camps", icon: Calendar, roles: ["founder", "manager", "executive"] },
  { to: "/goals", label: "Goals", icon: Flag, roles: ["founder", "manager", "executive"] },
  { to: "/scorecard", label: "Performance Scorecard", icon: TrendingUp, roles: ["founder", "manager", "executive"] },
  { to: "/targets", label: "Targets & Incentives", icon: Target, roles: ["founder", "manager"] },
  { to: "/executives", label: "Executive Master", icon: Users, roles: ["founder", "manager"] },
  { to: "/import", label: "Import & Digest", icon: Upload, roles: ["founder", "manager"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: ["founder", "manager", "executive"] },
  { to: "/ai", label: "AI Assistant", icon: Sparkles, roles: ["founder", "manager", "executive"] },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  if (!user) return null;
  const links = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <aside className="w-64 flex-shrink-0 border-r border-slate-800 bg-[#0B1120] hidden md:flex md:flex-col"
      data-testid="app-sidebar">
      <div className="h-16 px-6 flex items-center gap-3 border-b border-slate-800">
        <div className="w-9 h-9 rounded-lg bg-[#C9A24B] flex items-center justify-center">
          <Stethoscope size={20} className="text-[#0F172A]" />
        </div>
        <div>
          <div className="text-white text-sm font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
            Youngness Centre
          </div>
          <div className="text-[10px] text-slate-400 tracking-[0.15em] uppercase">Field CRM</div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {links.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`nav-${to.replace("/", "").replace(/\//g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive
                  ? "bg-[#C9A24B]/15 text-[#C9A24B] border border-[#C9A24B]/25"
                  : "text-slate-300 hover:bg-slate-800/60 hover:text-white border border-transparent"
              }`
            }
          >
            <Icon size={17} />
            <span className="font-medium">{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-slate-800 p-4">
        <div className="flex items-center gap-2.5 mb-2">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover border border-slate-700" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-white text-xs font-semibold">
              {user.name?.[0] || "?"}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm text-white font-medium truncate">{user.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#C9A24B]">{user.role}</div>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 truncate mb-2">{user.email}</div>
        <button
          data-testid="btn-logout"
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 text-xs text-slate-300 hover:text-white bg-slate-800/70 hover:bg-slate-700 rounded-lg py-2 transition-colors"
        >
          <LogOut size={13} /> Sign out
        </button>
      </div>
    </aside>
  );
}
